import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  TestTube,
  Plus,
  MagnifyingGlass,
  Funnel,
  DotsThree,
  Copy,
  Trash,
  Eye,
  PencilSimple,
  UploadSimple
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import ImportModal from '../../components/ImportModal';

const statusColors = {
  planned: 'status-planned',
  in_progress: 'status-in-progress',
  completed: 'status-completed',
  failed: 'status-failed'
};

const ExperimentsPage = () => {
  const navigate = useNavigate();
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    loadExperiments();
  }, [statusFilter]);

  const loadExperiments = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await api.getExperiments(params);
      setExperiments(data);
    } catch (error) {
      console.error('Failed to load experiments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async (id) => {
    try {
      const cloned = await api.cloneExperiment(id);
      navigate(`/experiments/${cloned.id}`);
    } catch (error) {
      console.error('Failed to clone experiment:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this experiment?')) {
      try {
        await api.deleteExperiment(id);
        setExperiments(experiments.filter(e => e.id !== id));
      } catch (error) {
        console.error('Failed to delete experiment:', error);
      }
    }
  };

  const filteredExperiments = experiments.filter(exp =>
    exp.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exp.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="experiments-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Experiments</h1>
          <p className="text-zinc-500 mt-1">Manage your lab experiments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={() => setShowImport(true)} data-testid="import-experiments-btn">
            <UploadSimple size={16} /> Import
          </Button>
          <Link to="/experiments/new">
            <Button className="btn-primary flex items-center gap-2" data-testid="new-experiment-btn">
              <Plus size={16} />
              New Experiment
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card-flat p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              type="text"
              placeholder="Search experiments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-field w-full"
              data-testid="search-experiments"
            />
          </div>
          <div className="flex items-center gap-2">
            <Funnel size={18} className="text-zinc-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="planned">Planned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Experiments Table */}
      <div className="card-flat overflow-hidden">
        {filteredExperiments.length > 0 ? (
          <table className="data-table" data-testid="experiments-table">
            <thead>
              <tr>
                <th className="table-header">Title</th>
                <th className="table-header">Status</th>
                <th className="table-header">Hypothesis</th>
                <th className="table-header">Created</th>
                <th className="table-header w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filteredExperiments.map((exp) => (
                <tr key={exp.id} className="table-row" data-testid={`experiment-row-${exp.id}`}>
                  <td className="table-cell">
                    <Link to={`/experiments/${exp.id}`} className="font-medium text-slate-100 hover:text-indigo-300">
                      {exp.title}
                    </Link>
                    {exp.tags?.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {exp.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="text-xs bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="table-cell">
                    <span className={statusColors[exp.status] || 'status-planned'}>
                      {exp.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="table-cell text-zinc-500 max-w-xs truncate">
                    {exp.hypothesis || '-'}
                  </td>
                  <td className="table-cell text-zinc-500">
                    {new Date(exp.created_at).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 hover:bg-zinc-100 rounded-sm" data-testid={`experiment-menu-${exp.id}`}>
                          <DotsThree size={20} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/experiments/${exp.id}`)}>
                          <Eye size={16} className="mr-2" /> View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/experiments/${exp.id}/edit`)}>
                          <PencilSimple size={16} className="mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleClone(exp.id)}>
                          <Copy size={16} className="mr-2" /> Clone
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(exp.id)} className="text-red-400">
                          <Trash size={16} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <div className="empty-state-image" />
            <h3 className="heading-card text-slate-100 mb-2">No experiments yet</h3>
            <p className="text-sm text-zinc-500 mb-4">Get started by creating your first experiment</p>
            <Link to="/experiments/new">
              <Button className="btn-primary">Create Experiment</Button>
            </Link>
          </div>
        )}
      </div>
      {showImport && <ImportModal type="experiments" onClose={() => setShowImport(false)} onImportComplete={loadExperiments} />}
    </div>
  );
};

export default ExperimentsPage;
